// The rules of colugo's resolvePickingJob that the "Resolve stock" dialog
// mirrors, so it can say why instead of offering a confirm that can only be
// refused.

import {
  MerchantOrderStatusEnum,
  PickingJobDeliveryErrorCode,
  PickingJobResolutionDestination,
  PickingJobSegment,
  PickingJobStatus,
} from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { getStatusTranslation } from '@/views/Order/utils/orderListFilters';
import { pickingJobStatusLabel } from './pickingJobLabels';

const { Warehouse: WAREHOUSE, Partner: PARTNER } =
  PickingJobResolutionDestination;

// Mirrors MAX_RESOLUTION_REASON_LENGTH in colugo's resolutionReason, which
// refuses a longer (trimmed) reason with INVALID_INPUT. Both count UTF-16 code
// units, as the textarea's maxLength and the dialog's counter do.
export const MAX_REASON_LENGTH = 1000;

// MIRROR of BLANK_REASON in backend/colugo/schema/Picking/utils/
// resolutionReason.ts — identical source, checked by colugo's
// resolutionReason.unit.test.ts. A reason of only White_Space,
// Default_Ignorable_Code_Point characters and U+2800 BRAILLE PATTERN BLANK is
// no reason, and colugo refuses it.
export const BLANK_REASON =
  /^[\p{White_Space}\p{Default_Ignorable_Code_Point}\u2800]*$/u;

// MIRROR of CONTROL_CHARACTER in the same colugo file: control characters
// other than tab, line feed, carriage return and U+0085, which colugo refuses.
export const CONTROL_CHARACTER = /(?![\t\n\r\u0085])\p{Cc}/u;

/** Whether colugo accepts `reason` as given (before its length cap). */
export function isAcceptableReason(reason: string): boolean {
  return !BLANK_REASON.test(reason) && !CONTROL_CHARACTER.test(reason);
}

// Mirrors WAREHOUSE_RESOLVABLE_STATUSES in colugo's PickingJobResolution. An
// event job may only go back before picking-confirm: after it the job carries
// a billable OrderFulfillment that has no credit-note path.
export const WAREHOUSE_JOB_STATUSES: Record<
  PickingJobSegment,
  PickingJobStatus[]
> = {
  [PickingJobSegment.OpenSystem]: [
    PickingJobStatus.PickingInProgress,
    PickingJobStatus.Picked,
    PickingJobStatus.DeliveryInProgress,
    PickingJobStatus.DeliveryIncomplete,
  ],
  [PickingJobSegment.Event]: [PickingJobStatus.PickingInProgress],
};

// The order statuses colugo can take to CANCELED, directly or through ERROR
// (ALLOWED_STATUS_TRANSITIONS in merchantOrderCoreUtils), plus CANCELED itself.
// Any other one on an owned order — SALES_ORDER_SENT, which only goes on to
// SENT_TO_PICKING — refuses the warehouse before anything moves.
const WAREHOUSE_ORDER_STATUSES: MerchantOrderStatusEnum[] = [
  MerchantOrderStatusEnum.Invalid,
  MerchantOrderStatusEnum.OrderReceived,
  MerchantOrderStatusEnum.Approved,
  MerchantOrderStatusEnum.SentToPicking,
  MerchantOrderStatusEnum.Picked,
  MerchantOrderStatusEnum.ErrorRetryable,
  MerchantOrderStatusEnum.InTransit,
  MerchantOrderStatusEnum.Delivered,
  MerchantOrderStatusEnum.ReturnToSender,
  MerchantOrderStatusEnum.OnHold,
  MerchantOrderStatusEnum.Error,
  MerchantOrderStatusEnum.Canceled,
];

// Mirrors PARTNER_DELIVERABLE_STATUSES in colugo's PickingJobResolution, plus
// the two it leaves as they are.
const PARTNER_ORDER_STATUSES: MerchantOrderStatusEnum[] = [
  MerchantOrderStatusEnum.Picked,
  MerchantOrderStatusEnum.InTransit,
  MerchantOrderStatusEnum.Error,
  MerchantOrderStatusEnum.ReturnToSender,
  MerchantOrderStatusEnum.Delivered,
  MerchantOrderStatusEnum.Canceled,
];

/** What the gates read about the job; see PickingJobActions' props. */
export interface JobFacts {
  segment: PickingJobSegment;
  status: PickingJobStatus;
  orderStatuses: MerchantOrderStatusEnum[];
  linkedOrderCount: number;
  resolved: boolean;
  hasBookedOrDeliveredRows: boolean;
}

// Why the whole action is unavailable, or null: the gates both destinations
// share, in colugo's order.
export function jobBlockedReason({
  status,
  resolved,
}: JobFacts): string | null {
  if (status === PickingJobStatus.PickingNotStarted) {
    return i18n.t('Picking:actionBlockedNotStarted');
  }
  if (resolved || status === PickingJobStatus.Released) {
    return i18n.t('Picking:actionResolveBlockedResolved');
  }
  if (status === PickingJobStatus.Delivered) {
    return i18n.t('Picking:actionResolveBlockedDelivered');
  }
  return null;
}

// Why one destination is unavailable, or null. Each mirrors a refusal of
// colugo's resolveToWarehouse / resolveToPartner, in the same order, so the
// dialog can say why instead of offering a confirm that can only be refused.
// Only refusals the page can see are mirrored: whether an event job's stock is
// booked to the event is answered by the backend alone.
function destinationBlockedReason(
  destination: PickingJobResolutionDestination,
  facts: JobFacts,
): string | null {
  const { segment, status, orderStatuses, linkedOrderCount } = facts;
  if (destination === WAREHOUSE) {
    if (!WAREHOUSE_JOB_STATUSES[segment].includes(status)) {
      return segment === PickingJobSegment.Event
        ? i18n.t('Picking:actionResolveWarehouseBlockedEvent')
        : i18n.t('Picking:actionResolveWarehouseBlockedStatus', {
            status: pickingJobStatusLabel(status),
          });
    }
    if (segment === PickingJobSegment.Event && facts.hasBookedOrDeliveredRows) {
      return i18n.t('Picking:actionResolveWarehouseBlockedEventBooked');
    }
    if (
      orderStatuses.length > 0 &&
      orderStatuses.every((s) => s === MerchantOrderStatusEnum.Delivered)
    ) {
      return i18n.t('Picking:actionResolveWarehouseBlockedOrdersDelivered');
    }
    const stuck = orderStatuses.find(
      (s) => !WAREHOUSE_ORDER_STATUSES.includes(s),
    );
    return stuck
      ? i18n.t('Picking:actionResolveWarehouseBlockedOrderStatus', {
          status: getStatusTranslation(stuck),
          canceled: getStatusTranslation(MerchantOrderStatusEnum.Canceled),
        })
      : null;
  }
  if (status === PickingJobStatus.PickingInProgress) {
    return i18n.t('Picking:actionResolvePartnerBlockedPicking');
  }
  if (orderStatuses.length === 0) {
    return linkedOrderCount === 0
      ? i18n.t('Picking:actionResolvePartnerBlockedNoOrders')
      : i18n.t('Picking:actionResolvePartnerBlockedOwnsNoOrder');
  }
  if (orderStatuses.every((s) => s === MerchantOrderStatusEnum.Canceled)) {
    return i18n.t('Picking:actionResolvePartnerBlockedAllCanceled');
  }
  const stuck = orderStatuses.find((s) => !PARTNER_ORDER_STATUSES.includes(s));
  return stuck
    ? i18n.t('Picking:actionResolvePartnerBlockedOrderStatus', {
        status: getStatusTranslation(stuck),
        delivered: getStatusTranslation(MerchantOrderStatusEnum.Delivered),
      })
    : null;
}

/** Each destination's blocked reason, or null where it is available. */
export function destinationBlockedReasons(
  facts: JobFacts,
): Record<PickingJobResolutionDestination, string | null> {
  return {
    [WAREHOUSE]: destinationBlockedReason(WAREHOUSE, facts),
    [PARTNER]: destinationBlockedReason(PARTNER, facts),
  };
}

/** What exactly a destination does, one consequence per bullet. */
export function destinationDetails(
  destination: PickingJobResolutionDestination,
  segment: PickingJobSegment,
): string[] {
  const labels = {
    picked: getStatusTranslation(MerchantOrderStatusEnum.Picked),
    inTransit: getStatusTranslation(MerchantOrderStatusEnum.InTransit),
    error: getStatusTranslation(MerchantOrderStatusEnum.Error),
    returnToSender: getStatusTranslation(
      MerchantOrderStatusEnum.ReturnToSender,
    ),
    delivered: getStatusTranslation(MerchantOrderStatusEnum.Delivered),
    canceled: getStatusTranslation(MerchantOrderStatusEnum.Canceled),
    released: pickingJobStatusLabel(PickingJobStatus.Released),
    deliveredJob: pickingJobStatusLabel(PickingJobStatus.Delivered),
  };
  if (destination === WAREHOUSE) {
    return [
      i18n.t('Picking:actionResolveWarehouseDetailStock'),
      i18n.t('Picking:actionResolveWarehouseDetailOrders', labels),
      i18n.t('Picking:actionResolveWarehouseDetailShipments'),
      i18n.t('Picking:actionResolveWarehouseDetailFinal', labels),
    ];
  }
  return [
    segment === PickingJobSegment.OpenSystem
      ? i18n.t('Picking:actionResolvePartnerDetailStockOpenSystem')
      : i18n.t('Picking:actionResolvePartnerDetailStockEvent'),
    i18n.t('Picking:actionResolvePartnerDetailOrders', labels),
    i18n.t('Picking:actionResolvePartnerDetailFinal', labels),
  ];
}

export type ErrorCode = PickingJobDeliveryErrorCode | 'INVALID_INPUT';

// What a refusal means, per code. The backend's reason is shown below it: it
// names the order that could not transition or the item that could not be
// moved, which is what ops needs to act on.
export const ERROR_MESSAGE_KEYS: Partial<Record<ErrorCode, string>> = {
  [PickingJobDeliveryErrorCode.JobAlreadyResolved]:
    'Picking:actionErrorJobAlreadyResolved',
  [PickingJobDeliveryErrorCode.JobAlreadyDelivered]:
    'Picking:actionErrorJobAlreadyDelivered',
  [PickingJobDeliveryErrorCode.EventStockNotBooked]:
    'Picking:actionErrorEventStockNotBooked',
  [PickingJobDeliveryErrorCode.LabelRetryInProgress]:
    'Picking:actionErrorLabelRetryInProgress',
  [PickingJobDeliveryErrorCode.StockNotBookable]:
    'Picking:actionErrorStockNotBookable',
  [PickingJobDeliveryErrorCode.StatusTransitionNotAllowed]:
    'Picking:actionErrorStatusTransitionNotAllowed',
  // Generic refusals; the reason says which gate it was.
  [PickingJobDeliveryErrorCode.InvalidStatus]: 'Picking:actionErrorRejected',
  [PickingJobDeliveryErrorCode.IncompletePickingBlocksConfirm]:
    'Picking:actionErrorRejected',
  INVALID_INPUT: 'Picking:actionErrorRejected',
};

export function isKnownErrorCode(code: string): code is ErrorCode {
  return code in ERROR_MESSAGE_KEYS;
}
